import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { getRatingsAnalytics } from '@app/services/analytics';
import { RatingsAnalyticsResponse } from '@app/models/analytics';

export const useAnalyticsRatings = () => {
  const navigate = useNavigate();
  const [analyticsData, setAnalyticsData] = useState<RatingsAnalyticsResponse['data'] | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchAnalyticsData = useCallback(async (params?: {
    startDate?: string;
    endDate?: string;
    connection?: string;
    period?: 'daily' | 'weekly' | 'monthly';
  }) => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await getRatingsAnalytics(params);
      setAnalyticsData(response.data.data);
    } catch (err) {
      setError('Failed to fetch analytics data');
      console.error('Error fetching analytics data:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  // Transform timeline data for ColumnChart
  const getChartData = (): (string | number)[][] => {
    if (!analyticsData?.timeline) {
      return [['Months', '5-star', '4-star', '3-star', '2-star', '1-star']];
    }

    return [
      ['Months', '5-star', '4-star', '3-star', '2-star', '1-star'],
      ...analyticsData.timeline.map((item) => [
        item.month,
        item.ratings[5] || 0,
        item.ratings[4] || 0,
        item.ratings[3] || 0,
        item.ratings[2] || 0,
        item.ratings[1] || 0,
      ]),
    ];
  };

  // Transform platform data for DonutChart
  const getPlatformChartData = (): (string | number)[][] => {
    if (!analyticsData?.platforms) {
      return [['Platform', 'Reviews']];
    }

    return [
      ['Platform', 'Reviews'],
      ...analyticsData.platforms.map((platform) => [
        platform.name,
        platform.count,
      ]),
    ];
  };

  // Transform average ratings for LineChart
  const getAverageRatingChartData = (): (string | number)[][] => {
    if (!analyticsData?.averageRatings) {
      return [['Month', 'Average Rating']];
    }

    return [
      ['Month', 'Average Rating'],
      ...analyticsData.averageRatings.map((item) => [
        item.month,
        item.average,
      ]),
    ];
  };

  return {
    analyticsData,
    loading,
    error,
    fetchAnalyticsData,
    chartData: getChartData(),
    platformChartData: getPlatformChartData(),
    averageRatingChartData: getAverageRatingChartData(),
    navigate,
  };
};
